import React, { useEffect } from 'react'
import { Checkbox, ChevronDown } from '@demandlocal/components'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { AnimatePresence, motion } from 'framer-motion'
import { useGetDealershipsByOrganizationId } from '../../../api/admin/useGetDealershipsByOrganizationId'
import classNames from 'classnames'
import { Dealership } from '../../../lib/types/Dealership'
import { useParams } from 'react-router'
import { useGetAgencyByAgencyId } from '../../../api/agencies/useGetAgencyByAgencyId'

function OrganizationCard(): JSX.Element {
  const { agency_id } = useParams()
  const { data: agency } = useGetAgencyByAgencyId(agency_id)
  const { setValue } = useFormContext()

  const organization = useWatch({
    name: 'organization',
  })

  const [isExpanded, setIsExpanded] = React.useState(true)

  const { data: allDealerships, isLoading } = useGetDealershipsByOrganizationId(
    agency?.id,
    {
      enabled: isExpanded && !organization?.has_all_dealerships_assigned,
    },
  )

  const { fields, replace } = useFieldArray({
    name: 'organization.dealerships',
    keyName: '_fieldId',
    shouldUnregister: false,
  })

  const handleAssignToAllChange = (checked: boolean) => {
    setValue('organization.has_all_dealerships_assigned', checked)

    if (checked) {
      setValue('organization.assigned_dealerships_count', agency?.dealership_count || 0)
    } else {
      if (allDealerships?.length) {
        const dealershipsWithCheckedState = allDealerships.map((dealership) => ({
          ...dealership,
          checked: false,
        }))
        replace(dealershipsWithCheckedState)
      }
      setValue('organization.assigned_dealerships_count', 0)
    }
  }

  const dealerships = useWatch({
    name: 'organization.dealerships',
  })

  useEffect(() => {
    if (!organization?.has_all_dealerships_assigned) {
      const assignedCount =
        dealerships?.filter((dealership) => dealership.checked)?.length || 0
      setValue('organization.assigned_dealerships_count', assignedCount)
    }
  }, [dealerships, organization?.has_all_dealerships_assigned, setValue])

  useEffect(() => {
    if (
      !organization?.has_all_dealerships_assigned &&
      allDealerships?.length &&
      allDealerships.length !== fields.length
    ) {
      const dealershipsWithCheckedState = allDealerships.map((dealership) => ({
        ...dealership,
        checked: false,
      }))
      replace(dealershipsWithCheckedState)
    }
  }, [allDealerships, fields.length, organization?.has_all_dealerships_assigned, replace])

  if (!agency) return <></>

  return (
    <motion.div className={'border-2 border-cultured-gray rounded-md'}>
      <motion.div
        layout={'position'}
        onClick={() => setIsExpanded((prev) => !prev)}
        className={'flex justify-between items-center cursor-pointer select-none p-3'}>
        <div className="flex flex-col">
          <h4 className={'font-bold text-sm'}>{agency.name}</h4>
          <span className="text-xs text-gray-500">
            {agency.dealership_count} Dealerships
          </span>
        </div>
        <div className={'flex gap-3 items-center text-xs'}>
          <span>assigned to</span>
          <h4 className={'font-bold'}>
            {`${organization?.assigned_dealerships_count}/${agency.dealership_count} dealerships`}
          </h4>
          <ChevronDown
            //   @ts-ignore
            className={'w-4 h-4 fill-black'}
          />
        </div>
      </motion.div>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            transition={{
              type: 'spring',
              stiffness: 170,
              damping: 26,
              mass: 1,
            }}
            initial="collapsed"
            exit="collapsed"
            animate={'open'}
            variants={{
              open: { opacity: 1, height: 'auto' },
              collapsed: { opacity: 0, height: 0 },
            }}
            className={'overflow-hidden relative'}>
            {isLoading && <div className={'absolute w-full h-full bg-white/50 z-10'} />}
            <div className={'flex flex-col p-3 gap-3'}>
              <div
                className={
                  'flex justify-between text-xs font-medium text-davy-gray items-center'
                }>
                <h4>Dealerships</h4>
                <div className={'flex gap-2 items-center'}>
                  <div>Assign to All</div>
                  <label
                    htmlFor={'assign_to_all'}
                    className="flex relative items-center cursor-pointer">
                    <input
                      onChange={(e) => handleAssignToAllChange(e.target.checked)}
                      checked={organization?.has_all_dealerships_assigned}
                      type="checkbox"
                      id={'assign_to_all'}
                      className="sr-only peer"
                    />
                    <div className="w-12 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-6 after:content-[''] after:absolute after:top-[4px] after:left-1 after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-persian-blue"></div>
                  </label>
                </div>
              </div>
              {!organization?.has_all_dealerships_assigned && (
                <div className={'flex flex-col gap-2'}>
                  {fields?.map((dealership, dealershipIndex) => {
                    const typedDealership = dealership as unknown as Dealership & {
                      _fieldId: string
                    }
                    return (
                      <div key={typedDealership._fieldId}>
                        <Checkbox
                          name={`organization.dealerships[${dealershipIndex}].checked`}
                          label={
                            <div>
                              <h4
                                className={'font-bold text-primary-persian-blue text-xs'}>
                                {typedDealership.dealership}
                              </h4>
                              <h3 className={'font-medium text-xs text-web-gray'}>
                                {[
                                  typedDealership.address,
                                  typedDealership.city,
                                  typedDealership.state,
                                  typedDealership.zip,
                                ]
                                  .filter(Boolean)
                                  .join(', ')}
                              </h3>
                            </div>
                          }
                        />
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

export default function UserDealerships({
  isAddNewUser = false,
}: {
  isAddNewUser?: boolean
}): JSX.Element {
  const containerClasses = classNames({
    'mt-12': isAddNewUser,
  })

  return (
    <div className={containerClasses}>
      <div className={'flex justify-between items-center'}>
        <h4 className={'font-bold text-base'}>Assign dealerships</h4>
      </div>
      <motion.div className={'flex flex-col gap-3 mt-4'}>
        <OrganizationCard />
      </motion.div>
    </div>
  )
}
